import { EventHandler } from "../HelperFunctions/index.js";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { addEntry, deleteEntry } from "./AMasterIndex.js";
import { SessionHandler } from "../APICommands/index.js";
import { AreaOfInterest } from "./AreaOfInterest.js";
import { Device } from "verdi-common/schemas/device/device";

export class ModelBase {
    /**
     * The last time this model was updated from the server
     * @type {Date}
     */
    lastRefreshed: Date;
    checkForRecentData = false;
    /**
     *
     * @type {Record<"irrigationAmount" | "moisture" | "valveState" | "avgNDVI" | "valveOpenPercent", {value: number, date: Date}>}
     */
    recentData = {};

    /**
     * A unique string identifying this model that is unique among all models
     * @type {String}
     */
    uid = undefined;

    /**
     * Completely identical to dbID (or databaseID)
     * @return {String}
     */
    get id() {
        return this.dbID;
    }
    /**
     * The database identifier for this model instance
     * @type {String}
     */
    dbID: string = undefined;

    /**
     * The common name of this model instance
     * @type {string}
     */
    name: string = undefined;

    /**
     * The id of the owner of this model
     * @type {string}
     */
    owner: string = undefined;

    /**
     * @type {AreaOfInterest}
     *
     * For some reason, it seems like this isn't a MapEntity, but is instead the model itself. Not sure why that is
     * but fixing it is out of scope.
     */
    linkedAreaOfInterest: AreaOfInterest = undefined;

    /**
     * @deprecated
     * @returns {string}
     */
    get label(): string {
        return this.name;
    }

    /**
     * @deprecated
     * @param v {string}
     */
    set label(v: string) {
        this.name = v;
    }

    /**
     * Fires whenever a new model is created for any reason
     * Event Args: {target: new model}
     * @type {EventHandler}
     */
    static onModelAdded = new EventHandler();
    /**
     * Fires when the event is changed locally and the change is successfully pushed to the server
     * Event Args: {target: this}
     * @type {EventHandler}
     */
    onChange = new EventHandler({ target: this });
    /**
     * Fires when the event is removed by the user and this removal is pushed successfully to the server
     * Event Args: {target: this}
     * @type {EventHandler}
     */
    onDelete = new EventHandler({ target: this });

    /**
     * Fires when the event is Loaded from the server originally
     * Event Args: {target: this}
     * @type {EventHandler}
     */
    onLoad = new EventHandler({ target: this });

    /**
     * Fires when the event is disposed of. The event *may* not have been deleted.
     * @type {EventHandler}
     */
    onDispose = new EventHandler({ target: this });

    loadPromise: Promise<void>;
    _loadPromiseResoloution: (v: unknown) => void;
    /**
     * Creates a new entity of this type and sends it to the server. When the server accepts the new entity, a new local entity
     * is created and this function returns.
     * @constructor
     * @return {Promise<ModelBase>}
     */
    // allow arguments for child classes where the CreateNew function which actually uses args
    static async createNew(..._args: unknown[]): Promise<ModelBase> {
        return new ModelBase({});
    }
    constructor(_json: unknown = {}, skip = false) {
        this._lastJSON = _json;
        this.loadPromise = new Promise((resolve) => {
            this._loadPromiseResoloution = resolve;
        }).then(() => {
            this._loadPromiseResoloution = undefined;
        });
        this.uid = generateUID("M");
        if (!skip) {
            ModelBase.onModelAdded.trigger({ target: this, modelAdded: this });
            this.lastRefreshed = new Date(Date.now());
        }
    }

    /**
     * What kind of model is this? Corresponds to the master index category it will be put in.
     * @type {String}
     */
    category;
    // optional json parameter to allow for child classes to pass in json
    async load(_json: unknown = {}) {
        addEntry(this.category, this);

        if (this.checkForRecentData && SessionHandler.recentData) {
            Object.keys(SessionHandler.recentData)
                .filter((k) => k.includes(this.dbID))
                .forEach((k) => {
                    const friendlyK = k.split("__")[0];
                    this.recentData[friendlyK] = SessionHandler.recentData[k];
                });
        }

        try {
            await this.onLoad.trigger({ target: this, load: this });
        } catch (e) {
            console.warn(e);
        }

        if (this._loadPromiseResoloution) this._loadPromiseResoloution(0);
    }
    deleted = false;
    beingDeleted = false;

    /**
     * Deletes this entity and returns when the entity has been deleted from the server.
     * @return {Promise<void>}
     */
    async delete(): Promise<void> {
        deleteEntry(this.category, this);
        this.onDelete.trigger({ target: this, delete: this }).then(() => {
            this.dispose();
        });
    }

    dispose(): void {
        this.onChange.clearListeners();
        this.onDelete.clearListeners();
        this.onLoad.clearListeners();
        this.onDispose.trigger({ target: this }).then(() => {
            this.onDispose.clearListeners();
        });
    }

    beingEdited = false;
    /**
     * Edits this entity on the server. Any changes made through this function will not affect the local instance until
     * they are updated on the server.
     *
     * @param {any[]} args
     * @return {Promise<ModelBase>}
     */
    // allow arguments for child classes where the edit function which actually uses args
    async edit(..._args: unknown[]): Promise<ModelBase> {
        this.onChange.trigger({ target: this, change: this });
        this.lastRefreshed = new Date(Date.now());
        return this;
    }

    /**
     * Edits this entity on the server. Any changes made through this function will not affect the local instance until
     * they are updated on the server.
     *
     * Second generation of this function
     * @return {Promise<ModelBase>}
     */
    async argEdit(..._args: unknown[]): Promise<ModelBase> {
        this.onChange.trigger({ target: this, change: this });
        return this;
    }
    // type left as Record<string, any> to allow extending this base class elsewhere
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _lastJSON: Record<string, any>;
    assignFromJSON(json: unknown): void {
        try {
            this._lastJSON = JSON.parse(JSON.stringify(json));
        } catch (e) {
            console.warn(e);
            this._lastJSON = { ...(json as Device) };
        }
    }
}
